import { useState, useEffect } from 'react';
import { Modal } from '../common/Modal';
import { MediumBadge } from '../common/Badge';
import { fmtPhone } from '../../utils/phone';
import type { Visit } from '../../types';

interface VisitDetailModalProps {
  visit: Visit | null;
  open: boolean;
  onClose: () => void;
  onEdit?: (visit: Visit) => void;
  onCancel?: (visit: Visit) => void;
  onSmsEnd?: (visit: Visit) => void;
  isPastVisit?: boolean;
}

export function VisitDetailModal({ visit, open, onClose, onEdit, onCancel, onSmsEnd, isPastVisit }: VisitDetailModalProps) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setCopied(false);
  }, [visit, open]);

  if (!visit) return null;

  const medium = visit.medium || 'video';
  const ts = visit.scheduledAt || visit.createdAt;
  const d = ts ? new Date(ts) : null;
  const dateStr = d ? d.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) : '';
  const timeStr = d ? d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '';

  const visitTime = visit.scheduledAt || visit.createdAt || 0;
  const isExpired = visitTime > 0 && visitTime < Date.now() - 60 * 60 * 1000;
  const isPast = visit.status === 'cancelled' || visit.status === 'completed' || visit.status === 'ended' || !!isPastVisit;

  let connectionHtml: React.ReactNode = null;
  const oneHourFromNow = Date.now() + 60 * 60 * 1000;
  const isWithinOneHour = !visit.scheduledAt || visit.scheduledAt <= oneHourFromNow;

  if (medium === 'video') {
    if (visit.joinUrl && visit.joinToken) {
      const fullUrl = `${window.location.origin}${visit.joinUrl}`;
      connectionHtml = (
        <>
          <div style={{ marginTop: '1rem' }}>
            <label className="text-sm" style={{ fontWeight: 500, display: 'block', marginBottom: '0.25rem' }}>Patient Link</label>
            <div className="link-box">
              <input type="text" value={fullUrl} readOnly id="visitDetailLink" />
              <button
                className="btn btn--outline btn--sm"
                onClick={() => {
                  navigator.clipboard.writeText(fullUrl).then(() => {
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  });
                }}
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>
          <div style={{ marginTop: '0.75rem' }}>
            <a href={`/visit?token=${visit.joinToken}&role=1`} target="_blank" rel="noopener noreferrer" className="btn btn--accent btn--sm">
              Join Visit
            </a>
          </div>
        </>
      );
    } else if (isExpired || isPast) {
      connectionHtml = <p className="text-sm text-muted" style={{ marginTop: '1rem' }}>This visit has expired.</p>;
    } else {
      connectionHtml = (() => {
        if (visit.scheduledAt) {
          const availAt = new Date(visit.scheduledAt - 60 * 60 * 1000);
          return <p className="text-sm text-muted" style={{ marginTop: '1rem' }}>Video link will be available at {availAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })} on {availAt.toLocaleDateString([], { month: 'short', day: 'numeric' })}<br />(1 hour before the visit)</p>;
        }
        return <p className="text-sm text-muted" style={{ marginTop: '1rem' }}>Video link is being set up.</p>;
      })();
    }
  } else if (medium === 'phone') {
    if (visit.dialIn) {
      connectionHtml = (
        <div className="dial-in" style={{ marginTop: '1rem' }}>
          <strong>Phone Dial-In</strong>
          <div style={{ marginTop: '0.25rem' }}>{fmtPhone(visit.dialIn.phone)}</div>
          <div>Your PIN: {visit.dialIn.providerPin || ''} · Patient PIN: {visit.dialIn.patientPin || visit.dialIn.pin || ''}</div>
        </div>
      );
    } else if (isExpired || isPast) {
      connectionHtml = <p className="text-sm text-muted" style={{ marginTop: '1rem' }}>This visit has expired.</p>;
    } else {
      connectionHtml = (() => {
        if (visit.scheduledAt) {
          const availAt = new Date(visit.scheduledAt - 60 * 60 * 1000);
          return <p className="text-sm text-muted" style={{ marginTop: '1rem' }}>Dial-in details will be available at {availAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })} on {availAt.toLocaleDateString([], { month: 'short', day: 'numeric' })}<br />(1 hour before the visit)</p>;
        }
        return <p className="text-sm text-muted" style={{ marginTop: '1rem' }}>Dial-in details are being set up.</p>;
      })();
    }
  } else if (medium === 'sms') {
    if (visit.isSmsVisit && (visit.status === 'active' || visit.twilioNumber)) {
      connectionHtml = (
        <div style={{ marginTop: '1rem' }}>
          <a href={`/chat/${visit.visitId || visit.roomId}`} target="_blank" rel="noopener noreferrer" className="btn btn--accent btn--sm">
            Open Chat
          </a>
          <div className="text-sm text-muted" style={{ marginTop: '0.25rem' }}>
            {visit.messageCount || 0} {(visit.messageCount || 0) === 1 ? 'message' : 'messages'}
          </div>
        </div>
      );
    } else if (isExpired || isPast) {
      connectionHtml = <p className="text-sm text-muted" style={{ marginTop: '1rem' }}>This visit has expired.</p>;
    } else {
      connectionHtml = (() => {
        if (visit.scheduledAt) {
          const activationTime = new Date(visit.scheduledAt - 60 * 60 * 1000);
          const timeStr = activationTime.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
          const dateStr = activationTime.toLocaleDateString([], { month: 'short', day: 'numeric' });
          return <p className="text-sm text-muted" style={{ marginTop: '1rem' }}>SMS will be activated at {timeStr} on {dateStr}.<br />(1 hour before the visit)</p>;
        }
        return <p className="text-sm text-muted" style={{ marginTop: '1rem' }}>SMS will be activated 1 hour before the visit.</p>;
      })();
    }
  }

  const contactParts = [visit.patientEmail, visit.patientPhone ? fmtPhone(visit.patientPhone) : null].filter(Boolean);
  const isUpcoming = !visit.isSmsVisit && !visit.joinUrl && !visit.dialIn && !!visit.scheduledAt;
  const cancelLabel = isUpcoming ? 'Cancel' : 'End';

  return (
    <Modal open={open} onClose={onClose} maxWidth="450px" ariaLabel={`${visit.patientName || 'Visit'} details`}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
        <h3 style={{ margin: 0 }}>{visit.patientName || 'Visit'}</h3>
        <MediumBadge medium={medium} />
      </div>
      <div className="text-sm text-muted">
        {dateStr && timeStr ? `${dateStr} at ${timeStr}` : 'Instant visit'}
        {visit.duration ? ` · ${visit.duration} min` : ''}
      </div>
      {visit.visitType && <div className="text-sm text-muted">{visit.visitType}</div>}
      {contactParts.length > 0 && (
        <div className="text-sm text-muted" style={{ marginTop: '0.25rem' }}>{contactParts.join(' · ')}</div>
      )}

      {connectionHtml}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1.25rem', paddingTop: '0.75rem', borderTop: '1px solid var(--border-color)' }}>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          {onEdit && (
            <a href="#" className="text-sm" style={{ color: 'var(--text-muted)', textDecoration: 'none' }} onClick={(e) => { e.preventDefault(); onClose(); onEdit(visit); }}>
              Edit
            </a>
          )}
          {(onCancel || onSmsEnd) && (
            <a
              href="#"
              className="text-sm"
              style={{ color: 'var(--error-color)', textDecoration: 'none' }}
              onClick={(e) => {
                e.preventDefault();
                onClose();
                if (visit.isSmsVisit && onSmsEnd) onSmsEnd(visit);
                else if (onCancel) onCancel(visit);
              }}
            >
              {cancelLabel}
            </a>
          )}
        </div>
        <button className="btn btn--primary btn--sm" onClick={onClose} style={{ marginLeft: 'auto' }}>Close</button>
      </div>
    </Modal>
  );
}
